// src/econ/model.ts — solveEcon: the pure labor & growth engine.
//
// PURE: inputs + baseline (+ params) in, outputs out. No I/O, no clock, no
// globals, no mutation. This is what makes INV-E1..E7 and CAL-1 testable, exactly
// like the capex solve(). All math is documented in docs/ECON_MODEL_SPEC.md.

import { ECON_PARAMS, type EconParams } from "./params.ts";
import type { EconBaseline, EconInputs, EconOutputs } from "./types.ts";

/** The numeric baseline the engine needs, extracted from the snapshot's fields. */
export interface EconBaselineValues {
  gdp: number;
  unemployment: number; // 0..1
  labor_share: number; // 0..1
  knowledge_worker_share: number; // 0..1
}

/** Pull the numeric baseline out of a provenance-carrying EconBaseline. */
export function baselineValues(b: EconBaseline): EconBaselineValues {
  return {
    gdp: b.gdp.value,
    unemployment: b.unemployment.value,
    labor_share: b.labor_share.value,
    knowledge_worker_share: b.knowledge_worker_share.value,
  };
}

const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);
/** Guard a power base: never raise a (tiny) negative to a fractional power. */
const pow = (base: number, exp: number): number => (base <= 0 ? 0 : Math.pow(base, exp));

/**
 * Solve the labor & growth scenario. `inputs` are the five user drivers + horizon;
 * `baseline` is the economy being perturbed; `params` are the calibrated
 * coefficients (overridable from the assumptions panel).
 */
export function solveEcon(
  inputs: EconInputs,
  baseline: EconBaselineValues,
  params: EconParams = ECON_PARAMS,
): EconOutputs {
  // --- clamp inputs to their declared domains (INV-E6) ---
  const kappa = clamp(inputs.capabilities, 0, 1);
  const alpha = clamp(inputs.adoption, 0, 1);
  const aut = clamp(inputs.autonomy, 0, 1);
  const pi = Math.max(1, inputs.productivity);
  const tau = Math.max(0, inputs.adjustment_months);
  const nu = clamp(inputs.new_task_share, 0, 1);

  const ks = clamp(baseline.knowledge_worker_share, 0, 1);
  const u0 = clamp(baseline.unemployment, 0, 1);
  const ls0 = clamp(baseline.labor_share, 0, 1);

  // --- task penetration (§2) ---
  const affected = kappa * alpha; // within knowledge tasks
  const automated = affected * aut;
  const augmented = affected * (1 - aut);
  const unchanged = 1 - affected;

  const A = ks * automated; // economy-wide automation penetration
  const X = ks * affected * (pi - 1); // AI-effect intensity

  // --- GDP (§3): concave response, 0 when X = 0 (INV-E1), monotone (INV-E7) ---
  const gdp_uplift_pct = params.gdpScale.v * pow(X, params.gdpExp.v);
  const gdp_level = baseline.gdp * (1 + gdp_uplift_pct);

  // --- factor shares (§4) ---
  const shift = Math.min(params.shiftCap.v, params.shiftScale.v * pow(A, params.shiftExp.v));
  const labor_share = clamp(ls0 * (1 - shift), 0, 1);
  const capital_share = 1 - labor_share;

  // --- wages (§5), vs a no-AI counterfactual ---
  const wage_knowledge = Math.max(
    -1,
    params.wageAug.v * augmented * (pi - 1) - params.wageAuto.v * automated,
  );
  const wage_other = Math.max(-1, params.wageSpill.v * gdp_uplift_pct);
  const wage_average = ks * wage_knowledge + (1 - ks) * wage_other;

  // --- reallocation, flows & unemployment (§6) ---
  const D = A * (1 - nu); // gross displacement, net of new-task re-absorption
  const rho = 1 / (1 + tau / 12); // reallocation rate: falls with adjustment time
  const crossed_over = clamp(D * rho, 0, 1);
  const displaced = clamp(D * (1 - rho) * params.permOut.v, 0, 1);
  const still_to_move = clamp(D * (1 - rho) * (1 - params.permOut.v), 0, 1);
  const stayed = clamp(1 - crossed_over - displaced - still_to_move, 0, 1);

  const extra = displaced + 0.5 * still_to_move;
  const u_knowledge = clamp(u0 + (ks > 0 ? extra / ks : extra), 0, 1);
  const u_other = clamp(u0 + 0.25 * crossed_over, 0, 1);
  const u_total = clamp(ks * u_knowledge + (1 - ks) * u_other, 0, 1);

  return {
    gdp_uplift_pct,
    gdp_level,
    labor_share,
    capital_share,
    unemployment: { knowledge: u_knowledge, other: u_other, total: u_total },
    worker_flows: { stayed, crossed_over, displaced, still_to_move },
    wage_change_pct: { knowledge: wage_knowledge, other: wage_other, average: wage_average },
    tasks: { unchanged, augmented, automated, new: nu },
  };
}
